/**
 * Enhanced Global Snackbar Context
 *
 * Provides a unified notification system across the entire application with advanced features:
 * - Notification queuing (sequential display)
 * - Custom icons per error type
 * - Context/title support
 * - Configurable position, duration, and styling
 * - Integration with error store
 * - Backward compatible with existing simple API
 *
 * Features:
 * - Auto-hide with configurable duration
 * - Support for success, error, warning, and info severities
 * - Error type classification (network, auth, server, validation)
 * - Notification queuing to prevent overlaps
 * - Consistent positioning and styling
 * - Simple API: showSuccess(), showError(), showWarning(), showInfo(), hide()
 * - Advanced API: show() with full options
 *
 * Usage (Simple):
 * ```tsx
 * import { useSnackbar } from '../contexts/SnackbarContext';
 *
 * const MyComponent = () => {
 *   const { showSuccess, showError } = useSnackbar();
 *
 *   const handleAction = async () => {
 *     try {
 *       await someOperation();
 *       showSuccess('Operation completed successfully!');
 *     } catch (error) {
 *       showError('Operation failed');
 *     }
 *   };
 * };
 * ```
 *
 * Usage (Advanced):
 * ```tsx
 * const { show } = useSnackbar();
 *
 * show({
 *   message: 'Connection lost',
 *   severity: 'error',
 *   errorType: 'network',
 *   context: 'Network Error',
 *   duration: 10000,
 *   position: { vertical: 'top', horizontal: 'right' }
 * });
 * ```
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Snackbar, Alert, AlertColor, AlertTitle, Typography, Box, Fade } from '@mui/material';
import {
  Error as ErrorIcon,
  Warning,
  Info,
  NetworkCheck,
  Security,
  CheckCircle
} from '@mui/icons-material';
import { ERROR_DISPLAY_CONFIG } from '../constants/errorHandling';

/**
 * Error types for classification and icon selection
 */
export type ErrorType = 'network' | 'auth' | 'server' | 'validation' | 'unknown';

/**
 * Notification position configuration
 */
export interface NotificationPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

/**
 * Individual notification configuration
 */
export interface Notification {
  /** Unique identifier */
  id: string;

  /** Notification message */
  message: string;

  /** Alert severity (determines color) */
  severity: AlertColor;

  /** Error type for icon mapping (optional) */
  errorType?: ErrorType;

  /** Context or title (optional) */
  context?: string;

  /** Auto-hide duration in milliseconds (optional, default: 6000) */
  duration?: number;

  /** Position (optional, default: bottom center) */
  position?: NotificationPosition;

  /** Show custom icon (optional, default: true) */
  showIcon?: boolean;

  /** Show close button (optional, default: true) */
  showClose?: boolean;

  /** Additional details for validation hints */
  details?: unknown;
}

/**
 * Options for simple notification methods
 */
export interface NotificationOptions {
  /** Error type for classification */
  errorType?: ErrorType;

  /** Context or title */
  context?: string;

  /** Custom duration */
  duration?: number;

  /** Custom position */
  position?: NotificationPosition;

  /** Additional details */
  details?: unknown;
}

interface SnackbarContextType {
  /** Show notification with full control */
  show: (notification: Omit<Notification, 'id'>) => string;

  /** Show success notification */
  showSuccess: (message: string, options?: NotificationOptions) => string;

  /** Show error notification */
  showError: (message: string, options?: NotificationOptions) => string;

  /** Show warning notification */
  showWarning: (message: string, options?: NotificationOptions) => string;

  /** Show info notification */
  showInfo: (message: string, options?: NotificationOptions) => string;

  /** Hide notification (current or specific ID) */
  hide: (id?: string) => void;

  /** Hide all notifications */
  hideAll: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

/**
 * Icon mapping for error types and severities
 */
const ICON_MAPPING: Record<ErrorType | AlertColor, React.ElementType> = {
  // Error types
  network: NetworkCheck,
  auth: Security,
  server: ErrorIcon,
  validation: Warning,
  unknown: Info,

  // Alert severities
  error: ErrorIcon,
  warning: Warning,
  info: Info,
  success: CheckCircle,
};

/**
 * Default position for backward compatibility
 */
const DEFAULT_POSITION: NotificationPosition = {
  vertical: 'bottom',
  horizontal: 'center'
};

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<Notification[]>([]);
  const [current, setCurrent] = useState<Notification | null>(null);
  const [displayedIds, setDisplayedIds] = useState<Set<string>>(new Set());

  /**
   * Process queue - show next notification when current is cleared
   */
  useEffect(() => {
    if (!current && queue.length > 0) {
      // Get next unshown notification from queue
      const nextNotification = queue.find(n => !displayedIds.has(n.id));

      if (nextNotification) {
        setCurrent(nextNotification);
        setDisplayedIds(prev => new Set(prev).add(nextNotification.id));
      } else {
        // All notifications have been shown, clear queue
        setQueue([]);
        setDisplayedIds(new Set());
      }
    }
  }, [current, queue, displayedIds]);

  /**
   * Show notification with full control
   */
  const show = useCallback((notification: Omit<Notification, 'id'>): string => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fullNotification: Notification = {
      ...notification,
      id,
      duration: notification.duration ?? ERROR_DISPLAY_CONFIG.NOTIFICATION_DURATION_MS,
      position: notification.position ?? DEFAULT_POSITION,
      showIcon: notification.showIcon ?? true,
      showClose: notification.showClose ?? true,
    };

    setQueue(prev => [...prev, fullNotification]);
    return id;
  }, []);

  /**
   * Show success notification
   */
  const showSuccess = useCallback((message: string, options?: NotificationOptions): string => {
    return show({
      message,
      severity: 'success',
      errorType: options?.errorType,
      context: options?.context,
      duration: options?.duration,
      position: options?.position,
      details: options?.details,
    });
  }, [show]);

  /**
   * Show error notification
   */
  const showError = useCallback((message: string, options?: NotificationOptions): string => {
    return show({
      message,
      severity: 'error',
      errorType: options?.errorType ?? 'unknown',
      context: options?.context,
      duration: options?.duration,
      position: options?.position,
      details: options?.details,
    });
  }, [show]);

  /**
   * Show warning notification
   */
  const showWarning = useCallback((message: string, options?: NotificationOptions): string => {
    return show({
      message,
      severity: 'warning',
      errorType: options?.errorType ?? 'validation',
      context: options?.context,
      duration: options?.duration,
      position: options?.position,
      details: options?.details,
    });
  }, [show]);

  /**
   * Show info notification
   */
  const showInfo = useCallback((message: string, options?: NotificationOptions): string => {
    return show({
      message,
      severity: 'info',
      errorType: options?.errorType,
      context: options?.context,
      duration: options?.duration,
      position: options?.position,
      details: options?.details,
    });
  }, [show]);

  /**
   * Hide notification
   */
  const hide = useCallback((id?: string) => {
    if (id) {
      // Hide specific notification
      if (current?.id === id) {
        setCurrent(null);
      }
      setQueue(prev => prev.filter(n => n.id !== id));
    } else {
      // Hide current notification
      setCurrent(null);
    }
  }, [current]);

  /**
   * Hide all notifications
   */
  const hideAll = useCallback(() => {
    setCurrent(null);
    setQueue([]);
    setDisplayedIds(new Set());
  }, []);

  /**
   * Handle snackbar close
   */
  const handleClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hide();
  }, [hide]);

  /**
   * Handle exit transition complete
   */
  const handleExited = useCallback(() => {
    setCurrent(null);
  }, []);

  /**
   * Get icon for notification
   */
  const getIcon = (notification: Notification): React.ReactElement | undefined => {
    if (!notification.showIcon) {
      return undefined;
    }

    // Prefer error type icon over severity icon
    const iconKey = notification.errorType ?? notification.severity;
    const IconComponent = ICON_MAPPING[iconKey];

    return IconComponent ? <IconComponent /> : undefined;
  };

  return (
    <SnackbarContext.Provider
      value={{
        show,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hide,
        hideAll,
      }}
    >
      {children}

      {current && (
        <Snackbar
          open={Boolean(current)}
          autoHideDuration={current.duration}
          onClose={handleClose}
          slots={{ transition: Fade }}
          slotProps={{ transition: { onExited: handleExited } }}
          anchorOrigin={current.position}
          sx={{
            '& .MuiSnackbarContent-root': {
              minWidth: 'auto',
              maxWidth: 500
            }
          }}
        >
          <Alert
            onClose={current.showClose ? handleClose : undefined}
            severity={current.severity}
            sx={{
              width: '100%',
              alignItems: 'flex-start'
            }}
            elevation={6}
            variant="filled"
            icon={getIcon(current)}
          >
            {current.context && (
              <AlertTitle sx={{ margin: 0, marginBottom: 0.5 }}>
                {current.context}
              </AlertTitle>
            )}

            <Typography variant="body2">
              {current.message}
            </Typography>

            {current.errorType === 'validation' && Boolean(current.details) && (
              <Box sx={{ marginTop: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Please check your input and try again.
                </Typography>
              </Box>
            )}
          </Alert>
        </Snackbar>
      )}
    </SnackbarContext.Provider>
  );
};

/**
 * Hook to access the global snackbar
 *
 * @throws Error if used outside of SnackbarProvider
 * @returns Snackbar methods: show, showSuccess, showError, showWarning, showInfo, hide, hideAll
 */
export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};

/**
 * Hook for manually triggering error notifications
 * Replaces useErrorNotification from ErrorNotification component
 *
 * @returns Function to show error notifications
 */
export const useErrorNotification = () => {
  const { showError } = useSnackbar();

  return useCallback((
    message: string,
    errorType?: ErrorType,
    context?: string
  ): string => {
    return showError(message, { errorType, context });
  }, [showError]);
};

/**
 * Hook for triggering success notifications
 * Replaces useSuccessNotification from ErrorNotification component
 *
 * @returns Function to show success notifications
 */
export const useSuccessNotification = () => {
  const { showSuccess } = useSnackbar();

  return useCallback((message: string, context?: string): string => {
    return showSuccess(message, { context });
  }, [showSuccess]);
};
